import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import "../css/shop.scss";

const formatPrice = (value) =>
  `${siteConfig.currency.symbol}${Number(value).toFixed(2)} ${siteConfig.currency.code}`;

const categoryGroups = {
  Tops: ["Outerwear", "Shirts", "Tees"],
  Bottoms: ["Pants", "Shorts", "Sweats"],
};

function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { wishlistItems, isWishlisted, toggleWishlist } = useContext(CartContext);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const filterCloseRef = useRef(null);

  const categories = useMemo(
    () => ["All", ...new Set(productData.map((product) => product.category))],
    []
  );
  const catalogMaxPrice = Math.ceil(
    Math.max(...productData.map((product) => Number(product.price)))
  );

  const selectedCategory = searchParams.get("category") || "All";
  const searchTerm = searchParams.get("search") || "";
  const sortBy = searchParams.get("sort") || "featured";
  const inStockOnly = searchParams.get("stock") === "true";
  const wishlistOnly = searchParams.get("wishlist") === "true";
  const maxPrice = Math.min(
    Number(searchParams.get("max")) || catalogMaxPrice,
    catalogMaxPrice
  );

  useEffect(() => {
    setIsLoading(true);
    const timer = window.setTimeout(() => setIsLoading(false), 140);
    return () => window.clearTimeout(timer);
  }, [searchParams]);

  useEffect(() => {
    if (!isFilterOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    const focusTimer = window.setTimeout(() => filterCloseRef.current?.focus(), 0);
    const handleFilterKeys = (event) => {
      if (event.key === "Escape") {
        setIsFilterOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = document.querySelectorAll(
        ".shop-filter button, .shop-filter input"
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleFilterKeys);
    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleFilterKeys);
      previousFocus?.focus?.();
    };
  }, [isFilterOpen]);

  const updateParam = (key, value, defaultValue) => {
    const next = new URLSearchParams(searchParams);
    if (value === defaultValue || value === "" || value === false) {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
    setSearchParams(next);
  };

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const allowedCategories = categoryGroups[selectedCategory] || [selectedCategory];
    const products = productData.filter((product) => {
      const categoryMatch =
        selectedCategory === "All" || allowedCategories.includes(product.category);
      const searchableText = [
        product.name,
        product.category,
        product.material,
        ...(product.tags || []),
      ]
        .join(" ")
        .toLowerCase();
      const searchMatch = !normalizedSearch || searchableText.includes(normalizedSearch);
      const stockMatch = !inStockOnly || product.inStock;
      const priceMatch = Number(product.price) <= maxPrice;
      const wishMatch = !wishlistOnly || wishlistItems.includes(product.id);
      return categoryMatch && searchMatch && stockMatch && priceMatch && wishMatch;
    });

    return [...products].sort((a, b) => {
      if (sortBy === "price-asc") return a.price - b.price;
      if (sortBy === "price-desc") return b.price - a.price;
      if (sortBy === "name-asc") return a.name.localeCompare(b.name);
      if (sortBy === "name-desc") return b.name.localeCompare(a.name);
      if (sortBy === "newest") return Number(b.newArrival) - Number(a.newArrival);
      return Number(b.featured) - Number(a.featured) || a.id - b.id;
    });
  }, [
    inStockOnly,
    maxPrice,
    searchTerm,
    selectedCategory,
    sortBy,
    wishlistOnly,
    wishlistItems,
  ]);

  const clearFilters = () => setSearchParams({});
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const filters = (
    <>
      <div className="shop-filter__mobile-heading">
        <h2>Filters</h2>
        <button
          ref={filterCloseRef}
          type="button"
          onClick={() => setIsFilterOpen(false)}
        >
          Close
        </button>
      </div>

      <fieldset>
        <legend>Product categories</legend>
        <label className="shop-filter__option">
          <input
            type="radio"
            name="category"
            checked={selectedCategory === "All"}
            onChange={() => updateParam("category", "All", "All")}
          />
          <span>Shop all</span>
          <small>{productData.length}</small>
        </label>
        {Object.entries(categoryGroups).map(([group, groupCategories]) => (
          <label className="shop-filter__option" key={group}>
            <input
              type="radio"
              name="category"
              checked={selectedCategory === group}
              onChange={() => updateParam("category", group, "All")}
            />
            <span>{group}</span>
            <small>
              {
                productData.filter((product) =>
                  groupCategories.includes(product.category)
                ).length
              }
            </small>
          </label>
        ))}
        {categories.slice(1).map((category) => (
          <label className="shop-filter__option" key={category}>
            <input
              type="radio"
              name="category"
              checked={selectedCategory === category}
              onChange={() => updateParam("category", category, "All")}
            />
            <span>{category}</span>
            <small>
              {productData.filter((product) => product.category === category).length}
            </small>
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend>Availability</legend>
        <label className="shop-filter__check">
          <input
            type="checkbox"
            checked={inStockOnly}
            onChange={(event) =>
              updateParam("stock", event.target.checked, false)
            }
          />
          <span>In stock only</span>
        </label>
        <label className="shop-filter__check">
          <input
            type="checkbox"
            checked={wishlistOnly}
            onChange={(event) =>
              updateParam("wishlist", event.target.checked, false)
            }
          />
          <span>My wishlist</span>
        </label>
      </fieldset>

      <fieldset>
        <legend>Price</legend>
        <div className="shop-filter__price-labels">
          <span>{siteConfig.currency.symbol}0</span>
          <span>
            Up to {siteConfig.currency.symbol}{maxPrice}
          </span>
        </div>
        <input
          className="shop-filter__range"
          type="range"
          min="0"
          max={catalogMaxPrice}
          step="5"
          value={maxPrice}
          aria-label="Maximum price"
          onChange={(event) =>
            updateParam("max", event.target.value, catalogMaxPrice)
          }
        />
      </fieldset>

      <button className="shop-filter__clear" type="button" onClick={clearFilters}>
        Clear all filters
      </button>
    </>
  );

  return (
    <main className="shop-page">
      <header className="shop-page__heading">
        <p className="eyebrow">Spring / Summer</p>
        <h1>{wishlistOnly ? "Your wishlist" : selectedCategory === "All" ? "Shop all" : selectedCategory}</h1>
        <p>
          Purposeful outdoor clothing, easy layers, and useful accessories for
          wherever the day wanders.
        </p>
      </header>

      <div className="shop-toolbar">
        <button
          className="shop-toolbar__filter"
          type="button"
          aria-expanded={isFilterOpen}
          onClick={() => setIsFilterOpen(true)}
        >
          Filters <span>({filteredProducts.length})</span>
        </button>
        <p>{filteredProducts.length} items</p>
        <label>
          <span>Sort by</span>
          <select
            value={sortBy}
            onChange={(event) => updateParam("sort", event.target.value, "featured")}
          >
            <option value="featured">Featured</option>
            <option value="newest">Newest</option>
            <option value="price-asc">Price, low to high</option>
            <option value="price-desc">Price, high to low</option>
            <option value="name-asc">Alphabetically, A–Z</option>
            <option value="name-desc">Alphabetically, Z–A</option>
          </select>
        </label>
      </div>

      <div className="shop-layout">
        {isFilterOpen && (
          <>
            <aside
              className="shop-filter is-open"
              role="dialog"
              aria-modal="true"
              aria-label="Product filters"
            >
              {filters}
            </aside>
            <button
              className="shop-filter__scrim"
              type="button"
              aria-label="Close product filters"
              onClick={() => setIsFilterOpen(false)}
            />
          </>
        )}

        <section className="shop-results" aria-live="polite" aria-busy={isLoading}>
          {isLoading ? (
            <div className="shop-grid shop-grid--loading" aria-label="Loading products">
              {Array.from({ length: 8 }, (_, index) => (
                <div className="shop-skeleton" key={index}>
                  <span />
                  <i />
                  <i />
                </div>
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="shop-grid">
              {filteredProducts.map((product) => (
                <article className="catalog-card" key={product.id}>
                  <div className="catalog-card__media">
                    <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
                      <img
                        src={product.image}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="catalog-card__sizes" aria-hidden="true">
                        {product.size.slice(0, 5).map((size) => (
                          <span key={size}>{size}</span>
                        ))}
                      </div>
                    </Link>
                    {product.compareAtPrice && (
                      <span className="catalog-card__sale">Sale</span>
                    )}
                    {!product.inStock && (
                      <span className="catalog-card__sold">Sold out</span>
                    )}
                    <button
                      className={`catalog-card__wish${
                        isWishlisted(product.id) ? " is-active" : ""
                      }`}
                      type="button"
                      aria-label={
                        isWishlisted(product.id)
                          ? `Remove ${product.name} from wishlist`
                          : `Add ${product.name} to wishlist`
                      }
                      aria-pressed={isWishlisted(product.id)}
                      onClick={() => toggleWishlist(product.id)}
                    >
                      <span aria-hidden="true">♡</span>
                    </button>
                  </div>
                  <div className="catalog-card__copy">
                    <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
                      {product.name}
                    </Link>
                    <p>
                      {product.compareAtPrice && (
                        <del>{formatPrice(product.compareAtPrice)}</del>
                      )}
                      <span className={product.compareAtPrice ? "is-sale" : ""}>
                        {formatPrice(product.price)}
                      </span>
                    </p>
                    <small>{product.colors.length} colors</small>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="shop-empty">
              <p className="eyebrow">Nothing found</p>
              <h2>No gear matches those filters.</h2>
              <p>Try widening the price range or clearing the current selection.</p>
              <button className="button button--primary" type="button" onClick={clearFilters}>
                Reset filters
              </button>
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

export default Shop;
